import ScreenWrapper from '@components/layout/screen-wrapper';
import { BrandLogo, Button, Field, Input, Text } from '@components/ui';
import {
  type ForgotPasswordFormValues,
  forgotPasswordSchema,
} from '@features/auth/schemas/forgotPasswordSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AuthSplitCard } from './auth-split-card';

interface ResetPasswordProps {
  onSubmit?: (values: ForgotPasswordFormValues) => void;
}

const ResetPassword = ({
  onSubmit: _onSubmit,
}: ResetPasswordProps): JSX.Element => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ForgotPasswordFormValues>({
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
    resolver: zodResolver(forgotPasswordSchema),
    mode: 'onBlur',
  });

  const onSubmit = (values: ForgotPasswordFormValues): void => {
    void values;
  };

  return (
    <ScreenWrapper
      pageSx={{
        height: '100dvh',
        maxHeight: '100dvh',
        width: '100%',
        overflow: 'auto',
      }}
      containerSx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        height: 'auto',
        width: '100%',
      }}
    >
      <Box
        sx={{
          m: 'auto',
          display: 'flex',
          width: '100%',
          justifyContent: 'center',
          py: 3,
        }}
      >
        <AuthSplitCard>
          <Box
            component="form"
            sx={{ width: '100%' }}
            onSubmit={handleSubmit(onSubmit)}
          >
            <BrandLogo height={48} sx={{ mx: 'auto', mb: 2 }} />
            <Text variant="title" align="center" sx={{ mb: 3 }}>
              {t('auth.resetPassword')}
            </Text>
            <Field
              sx={{ mb: 2.5 }}
              label={t('auth.newPassword')}
              error={
                errors.newPassword
                  ? t(errors.newPassword.message ?? '')
                  : undefined
              }
            >
              <Input
                type="password"
                invalid={Boolean(errors.newPassword)}
                placeholder={t('auth.newPasswordPlaceholder')}
                autoComplete="new-password"
                {...register('newPassword')}
              />
            </Field>
            <Field
              sx={{ mb: 2.5 }}
              label={t('auth.confirmPassword')}
              error={
                errors.confirmPassword
                  ? t(errors.confirmPassword.message ?? '')
                  : undefined
              }
            >
              <Input
                type="password"
                invalid={Boolean(errors.confirmPassword)}
                placeholder={t('auth.confirmPasswordPlaceholder')}
                autoComplete="new-password"
                {...register('confirmPassword')}
              />
            </Field>
            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                disabled={!isValid}
              >
                {t('common.done')}
              </Button>
            </Box>
          </Box>
        </AuthSplitCard>
      </Box>
    </ScreenWrapper>
  );
};
export default ResetPassword;
